import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import gridSelector from 'common/components/grid-view/controllers/selectors';
import { makeSelectAplDetail } from 'pages/product-apl-detail/controllers/selectors';

import selectorUser from '@redux/user/selectors';
import * as selectors from 'pages/company-profile/controllers/selectors';
import * as assetSelectors from 'pages/asset-full-view/controllers/selectors';
import * as companySelectors from 'pages/company/controllers/selectors';

import { useGetProductFullView } from './product-full/hooks';
import { useCheckIsSuperAdmin } from './useCheckIsSuperUser';
import { useCheckPermissions } from './useCheckPermissions';
import { PERMISSION_EDIT_QA_SPEC } from 'pages/qa-spec/hooks';
import {
  MEMBER_TYPE_MANUFACTURER,
  MEMBER_TYPE_SHARED_ONLY,
} from 'static/Constants';
import { useSafeProductIdParam } from './useProductFullView';

export const useCheckPrivateLabel = () => {
  const { id } = useSafeProductIdParam();

  const currentPathName = window.location.pathname;
  const productFullViewRegex = /^\/product\/.*/;

  const isProductFullView = productFullViewRegex.test(currentPathName);

  const productId = isProductFullView ? id : null;

  const { productFull } = useGetProductFullView({
    productId,
  });

  const checkPrivateLabel = () => {
    return productFull?.isPrivateLabel;
  };
  return {
    isProductFullView,
    checkPrivateLabel,
  };
};

export const useCheckDisableDownloadProduct = () => {
  const productGrid = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );

  const userInfo = useSelector(selectorUser.makeSelectUserInfo());

  const hasEditQaSpecPermission = useCheckPermissions(PERMISSION_EDIT_QA_SPEC);

  const memberType = userInfo?.member?.memberType;

  const isSupplier =
    MEMBER_TYPE_SHARED_ONLY === memberType ||
    MEMBER_TYPE_MANUFACTURER === memberType;

  const { id } = useSafeProductIdParam();

  const isSuperAdmin = useCheckIsSuperAdmin();
  const { productFull } = useGetProductFullView({ productId: id });

  const { isPrivateLabel, suppliers } = productFull;

  const [firstSupplier] = suppliers ?? [];

  const checkDisableDownloadProductFull = () => {
    if (isSuperAdmin) return false;

    if (productFull?.isOwner && isPrivateLabel) return false;

    if (!isSupplier || !isPrivateLabel) return true;

    if (firstSupplier?.id !== userInfo?.member?.id || !hasEditQaSpecPermission)
      return true;

    return false;
  };

  const checkDisableDownloadProductGrid = () => {
    if (isSuperAdmin) return false;

    return productGrid.some((product) => {
      const { isPrivateLabel, isOwner, assignTo } = product;

      if (isOwner && isPrivateLabel) return false;

      if (!isSupplier || !isPrivateLabel) return true;

      if (assignTo !== userInfo?.member?.id || !hasEditQaSpecPermission) {
        return true;
      }

      return false;
    });
  };

  const disableDownloadProduct = id
    ? checkDisableDownloadProductFull()
    : checkDisableDownloadProductGrid();

  return {
    disableDownloadProduct,
  };
};

export const useCheckDisableDownloadAPL = () => {
  const aplGrid = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );
  const aplDetail = useSelector(makeSelectAplDetail());

  const isSuperAdmin = useCheckIsSuperAdmin();

  const checkDisableDownloadAPLDetail = (aplPropsData = null) => {
    if (isSuperAdmin) return false;

    const apl = aplPropsData || aplDetail;

    return !apl?.isOwner;
  };

  const checkDisableDownloadAPLGrid = () => {
    if (isSuperAdmin) return false;

    return aplGrid?.some((product) => !product?.isOwner);
  };

  return {
    checkDisableDownloadAPLDetail,
    checkDisableDownloadAPLGrid,
  };
};

export const useCheckDisableDownloadAsset = () => {
  const isSuperAdmin = useCheckIsSuperAdmin();

  const assetFullData = useSelector(assetSelectors.assetDataLoading());
  const selectedAsset = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );
  const { checkPrivateLabel, isProductFullView } = useCheckPrivateLabel();

  const isPrivateLabel = checkPrivateLabel();

  const checkDisableDownloadAssetDetail = (assetPropsData = null) => {
    if (isSuperAdmin) return false;

    if (isProductFullView && !isPrivateLabel) return true;

    const assetDetail = assetPropsData || assetFullData;
    return !assetDetail?.isOwner;
  };

  const checkDisableDownloadAssetGrid = () => {
    if (isSuperAdmin) return false;

    return selectedAsset?.some((asset) => !asset?.isOwner);
  };

  return {
    checkDisableDownloadAssetDetail,
    checkDisableDownloadAssetGrid,
  };
};

export const useCheckDisableDownloadMember = () => {
  const isSuperAdmin = useCheckIsSuperAdmin();

  const selectedMember = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );

  const memberInfo = useSelector(selectors.selectMemberProfile());
  const companyInfo = useSelector(companySelectors.setlectCompanyInfo());

  const checkDisableDownloadMemberGrid = () => {
    if (isSuperAdmin) return false;

    return selectedMember?.some((member) => !member?.isOwner);
  };

  const checkDisableDownloadMemberDetail = () => {
    if (isSuperAdmin) return false;

    const currentCompany = companyInfo || memberInfo;

    return !currentCompany?.isOwner;
  };

  return {
    checkDisableDownloadMemberGrid,
    checkDisableDownloadMemberDetail,
  };
};

export const useCheckDisableDownloadFolder = () => {
  const isSuperAdmin = useCheckIsSuperAdmin();

  const checkDisableDownloadFolderGrid = (folders) => {
    if (isSuperAdmin) return false;

    return folders?.some((folder) => !folder?.isOwner);
  };

  const checkDisableDownloadFolderDetail = (folderDetail) => {
    if (isSuperAdmin) return false;

    return !folderDetail?.isOwner;
  };

  return {
    checkDisableDownloadFolderGrid,
    checkDisableDownloadFolderDetail,
  };
};
